import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { eventsAPI, uploadAPI, userSettingsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ComboboxWithAdd } from '../components/ui/combobox-with-add';
import { EVENT_TYPES, EVENT_SIZES, US_STATES, cn, normalizeState } from '../lib/utils';
import { toast } from 'sonner';
import { format, parseISO, isValid } from 'date-fns';
import { CalendarIcon, Loader2, Save, ArrowLeft, Upload, X, Image, Plus, Clock, Users, Star, FileText, AlertCircle } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { EventRating } from '../components/EventRating';
import { UpdateRequestDialog } from '../components/UpdateRequestDialog';

const DEFAULT_VENUE_TYPES = [
  "Indoor", "Outdoor", "Expo Center", "Fairgrounds", 
  "Community Center", "Church/School", "Park", "Street Festival"
];

export const EditEvent = () => {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [customCategory, setCustomCategory] = useState('');
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [venueTypes, setVenueTypes] = useState(DEFAULT_VENUE_TYPES);
  const [dateWarnings, setDateWarnings] = useState([]);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);

  /**
   * Safely parse an ISO-ish date string to a Date object.
   * Returns null for empty, invalid, or suspiciously old (<2000) values
   * rather than letting the calendar pickers explode on an Invalid Date
   * (which was surfacing as "year 1905" on bulk-imported malformed dates).
   */
  const safeParseDate = (raw) => {
    if (!raw) return null;
    try {
      const d = parseISO(String(raw));
      if (!isValid(d)) return null;
      if (d.getFullYear() < 2000) return null;
      return d;
    } catch {
      return null;
    }
  };
  
  const [formData, setFormData] = useState({
    title: '',
    event_type: '',
    location: '',
    city: '',
    state: '',
    date: null,
    end_date: null,
    date_hours: '',  // Hours for primary date range
    start_time: '',  // Daily start time (HH:MM)
    end_time: '',    // Daily end time (HH:MM)
    event_dates: [], // Array of {start_date, end_date, hours}
    deadline: null,
    booth_cost: '',
    event_size: '',
    venue_type: '',
    description: '',
    website_url: '',
    signup_url: '',  // Application/signup URL
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    image_url: '',
    is_community: false,  // Share with community
    event_rating: 0,  // User's rating 1-5
    personal_notes: '',  // Personal notes about the event
    recurring_schedule: null,  // For farmers markets
  });

  // Recurring schedule state for farmers markets
  const [recurringDays, setRecurringDays] = useState([]);
  const [recurringFrequency, setRecurringFrequency] = useState('every');

  // True when the loaded event is a community event — used to gate the form
  // for non-admin users, who can only request changes via email.
  const [eventIsCommunity, setEventIsCommunity] = useState(false);

  const isFarmersMarket = formData.event_type === 'farmers_market';

  const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const FREQUENCY_OPTIONS = [
    { value: 'every', label: 'Every week' },
    { value: 'every_2nd', label: 'Every 2nd week' },
    { value: 'every_3rd', label: 'Every 3rd week' },
    { value: '1st_and_3rd', label: '1st & 3rd of the month' },
    { value: '2nd_and_4th', label: '2nd & 4th of the month' },
  ];

  const computeRecurringPreview = () => {
    if (!isFarmersMarket || !formData.date || !formData.end_date || recurringDays.length === 0) return [];
    const start = new Date(format(formData.date, 'yyyy-MM-dd') + 'T00:00:00');
    const end = new Date(format(formData.end_date, 'yyyy-MM-dd') + 'T00:00:00');
    if (end < start) return [];
    const result = [];
    const current = new Date(start);
    while (current <= end) {
      const jsDay = current.getDay();
      const pyDay = jsDay === 0 ? 6 : jsDay - 1;
      if (recurringDays.includes(pyDay)) {
        let include = false;
        if (recurringFrequency === 'every') include = true;
        else if (recurringFrequency === 'every_2nd' || recurringFrequency === 'every_3rd') {
          const daysDiff = Math.floor((current - start) / (1000 * 60 * 60 * 24));
          const weekNum = Math.floor(daysDiff / 7);
          const skip = recurringFrequency === 'every_2nd' ? 2 : 3;
          include = weekNum % skip === 0;
        } else if (recurringFrequency === '1st_and_3rd' || recurringFrequency === '2nd_and_4th') {
          const weekOfMonth = Math.floor((current.getDate() - 1) / 7) + 1;
          include = recurringFrequency === '1st_and_3rd' ? [1, 3].includes(weekOfMonth) : [2, 4].includes(weekOfMonth);
        }
        if (include) result.push(new Date(current));
      }
      current.setDate(current.getDate() + 1);
    }
    return result;
  };

  const recurringPreview = computeRecurringPreview();

  // Add additional date range
  const addDateRange = () => {
    setFormData(prev => ({
      ...prev,
      event_dates: [...prev.event_dates, { start_date: null, end_date: null, hours: '' }]
    }));
  };

  // Remove date range
  const removeDateRange = (index) => {
    setFormData(prev => ({
      ...prev,
      event_dates: prev.event_dates.filter((_, i) => i !== index)
    }));
  };

  // Update date range
  const updateDateRange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      event_dates: prev.event_dates.map((range, i) => 
        i === index ? { ...range, [field]: value } : range
      )
    }));
  };

  const handleAddVenueType = async (newType) => {
    try {
      const response = await userSettingsAPI.addValue('venue_types', newType);
      setVenueTypes(response.data.values);
      toast.success(`Venue type "${newType}" added`);
    } catch (error) {
      toast.error('Failed to add venue type');
    }
  };

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        // Fetch venue types and event in parallel
        const [eventRes, venueTypesRes] = await Promise.all([
          eventsAPI.getOne(id),
          userSettingsAPI.get('venue_types')
        ]);
        const event = eventRes.data;
        setVenueTypes(venueTypesRes.data.values || DEFAULT_VENUE_TYPES);
        setEventIsCommunity(!!event.is_community);
        
        // Users can edit any event they have saved (favorited)
        // No ownership check needed - if user can access this page, they can edit

        // Check if it's a custom category
        const isCustomCategory = !EVENT_TYPES[event.event_type];
        if (isCustomCategory) {
          setShowCustomCategory(true);
          setCustomCategory(event.event_type);
        }

        // Track fields whose stored date was invalid so we can warn the user.
        const warn = [];
        const parsedDate = safeParseDate(event.date);
        if (event.date && !parsedDate) warn.push({ field: 'Start date', raw: event.date });
        const parsedEndDate = safeParseDate(event.end_date);
        if (event.end_date && !parsedEndDate) warn.push({ field: 'End date', raw: event.end_date });
        const parsedDeadline = safeParseDate(event.deadline);
        if (event.deadline && !parsedDeadline) warn.push({ field: 'Deadline', raw: event.deadline });
        setDateWarnings(warn);

        setFormData({
          title: event.title || '',
          event_type: isCustomCategory ? 'custom' : event.event_type || '',
          location: event.location || '',
          city: event.city || '',
          state: normalizeState(event.state) || '',
          date: parsedDate,
          end_date: parsedEndDate,
          date_hours: '',  // Will be extracted from event_hours if present
          start_time: event.start_time || '',
          end_time: event.end_time || '',
          event_dates: event.event_dates ? event.event_dates.map(d => ({
            start_date: safeParseDate(d.start_date),
            end_date: safeParseDate(d.end_date),
            hours: ''
          })) : [],
          deadline: parsedDeadline,
          booth_cost: event.booth_cost?.toString() || '',
          event_size: event.event_size || '',
          venue_type: event.venue_type || '',
          description: event.description || '',
          website_url: event.website_url || '',
          signup_url: event.signup_url || '',
          contact_name: event.contact_name || '',
          contact_phone: event.contact_phone || '',
          contact_email: event.contact_email || '',
          image_url: event.image_url || '',
          is_community: event.is_community || false,
          event_rating: event.event_rating || 0,
          personal_notes: event.personal_notes || '',
          recurring_schedule: event.recurring_schedule || null,
        });
        
        // Restore recurring schedule state
        if (event.recurring_schedule) {
          setRecurringDays(event.recurring_schedule.days || []);
          setRecurringFrequency(event.recurring_schedule.frequency || 'every');
        }
        
        // Parse existing event_hours string back into date_hours if simple format
        if (event.event_hours && !event.event_hours.includes(':')) {
          setFormData(prev => ({ ...prev, date_hours: event.event_hours }));
        }
        
        if (event.image_url) {
          setImagePreview(event.image_url);
        }
      } catch (error) {
        toast.error('Event not found');
        navigate('/events');
      } finally {
        setLoading(false);
      }
    };

    if (authLoading) {
      // Auth is still hydrating — don't redirect; just wait.
      return;
    }
    if (user) {
      fetchEvent();
    } else {
      navigate('/login');
    }
  }, [id, user, authLoading, navigate]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Handle custom category selection
    if (field === 'event_type') {
      if (value === 'custom') {
        setShowCustomCategory(true);
      } else {
        setShowCustomCategory(false);
        setCustomCategory('');
      }
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      handleChange('image_url', response.data.url);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
      setImagePreview(formData.image_url);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    handleChange('image_url', '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.city || !formData.state || !formData.date) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate custom category
    const eventType = formData.event_type === 'custom' ? customCategory.trim() : formData.event_type;
    if (!eventType) {
      toast.error('Please select or enter an event category');
      return;
    }

    setSaving(true);
    try {
      // Format event dates
      const formattedEventDates = formData.event_dates
        .filter(d => d.start_date)
        .map(d => ({
          start_date: format(d.start_date, 'yyyy-MM-dd'),
          end_date: d.end_date ? format(d.end_date, 'yyyy-MM-dd') : null
        }));

      // Build event_hours string from per-day hours
      const hoursEntries = [];
      if (formData.date && formData.date_hours) {
        const dateLabel = formData.end_date 
          ? `${format(formData.date, 'EEE MMM d')}-${format(formData.end_date, 'd')}`
          : format(formData.date, 'EEE MMM d');
        hoursEntries.push(`${dateLabel}: ${formData.date_hours}`);
      }
      formData.event_dates.forEach(d => {
        if (d.start_date && d.hours) {
          const dateLabel = d.end_date
            ? `${format(d.start_date, 'EEE MMM d')}-${format(d.end_date, 'd')}`
            : format(d.start_date, 'EEE MMM d');
          hoursEntries.push(`${dateLabel}: ${d.hours}`);
        }
      });
      const event_hours = hoursEntries.length > 0 ? hoursEntries.join(', ') : null;

      // Check if event has passed - auto-disable community sharing for past events
      const eventEndDate = formData.end_date || formData.date;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const hasEventPassed = eventEndDate && new Date(eventEndDate) < today;

      const eventData = {
        ...formData,
        event_type: eventType,
        date: formData.date ? format(formData.date, 'yyyy-MM-dd') : null,
        end_date: formData.end_date ? format(formData.end_date, 'yyyy-MM-dd') : null,
        event_dates: formattedEventDates.length > 0 ? formattedEventDates : null,
        deadline: formData.deadline ? format(formData.deadline, 'yyyy-MM-dd') : null,
        booth_cost: formData.booth_cost ? parseFloat(formData.booth_cost) : null,
        event_hours,
        start_time: formData.start_time || null,
        end_time: formData.end_time || null,
        is_community: hasEventPassed ? false : formData.is_community, // Disable community for past events
        recurring_schedule: isFarmersMarket && recurringDays.length > 0 ? {
          days: recurringDays,
          frequency: recurringFrequency,
        } : null,
      };
      
      // Remove internal-only fields
      delete eventData.date_hours;
      
      await eventsAPI.update(id, eventData);
      toast.success('Event updated successfully!');
      navigate(`/events/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1E3A5F] animate-spin" />
      </div>
    );
  }

  // Community events are admin-only for editing. Regular users see a
  // friendly gate with a dialog to submit a structured update request.
  if (eventIsCommunity && !user?.is_admin) {
    const subject = encodeURIComponent(`Community event update request — ID ${id}`);
    const body = encodeURIComponent(
      `Hi AMSCompass team,\n\n` +
      `I'd like to request an update to this community event:\n\n` +
      `Event ID: ${id}\n` +
      `Title: ${formData.title || '(unknown)'}\n` +
      `Link: ${typeof window !== 'undefined' ? window.location.origin : ''}/events/${id}\n\n` +
      `Requested change:\n\n\nThanks!`
    );
    return (
      <>
        <div className="min-h-screen py-12 px-6 flex items-start justify-center" data-testid="edit-event-admin-gate">
          <div className="max-w-lg w-full bg-white border-2 border-amber-200 rounded-2xl p-8 shadow-sm text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
              <AlertCircle className="w-7 h-7 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold font-['Outfit'] text-[#1E3A5F]">This is a community event</h1>
            <p className="text-[#4A4A4A]">
              Community events can only be edited by AMSCompass admins to keep
              shared data trustworthy. If something needs a change, send us the
              details and we&rsquo;ll review it.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 pt-2 justify-center">
              <Button
                type="button"
                className="btn-primary"
                onClick={() => setUpdateDialogOpen(true)}
                data-testid="edit-event-request-update-btn"
              >
                Request an update
              </Button>
              <Link to={`/events/${id}`}>
                <Button type="button" variant="outline" className="border-[#1E3A5F] text-[#1E3A5F]">
                  Back to event
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <UpdateRequestDialog
          open={updateDialogOpen}
          onOpenChange={setUpdateDialogOpen}
          eventId={id}
          eventTitle={formData.title}
          mailtoFallbackHref={`mailto:support@amscompass.com?subject=${subject}&body=${body}`}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6 md:px-12">
      <div className="max-w-3xl mx-auto">
        <Link
          to={`/events/${id}`}
          className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1E3A5F] mb-6 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to event
        </Link>

        <div className="card-dashboard" style={{ boxShadow: '6px 6px 0px 0px #1E3A5F' }}>
          <div className="mb-8">
            <h1 className="text-3xl font-bold font-['Outfit'] text-[#1E3A5F] mb-2">Edit Event</h1>
            <p className="text-[#4A4A4A]">Update your event details</p>
          </div>

          {dateWarnings.length > 0 && (
            <div
              className="mb-6 flex items-start gap-3 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl"
              data-testid="edit-event-date-warning"
            >
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold">
                  {dateWarnings.length === 1 ? 'One date was stored in an invalid format' : 'Some dates were stored in an invalid format'}
                </p>
                <ul className="text-amber-800/90 mt-1 list-disc list-inside">
                  {dateWarnings.map((w, idx) => (
                    <li key={idx}>
                      {w.field}: <code className="bg-amber-100 px-1 rounded">{String(w.raw)}</code>
                    </li>
                  ))}
                </ul>
                <p className="text-amber-800/90 mt-2">
                  We cleared those fields so the page doesn&rsquo;t crash. Pick a new date from the
                  calendar below and save to fix it.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Info */}
            <div className="space-y-6">
              <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] pb-2 border-b-2 border-[#E5E5E5]">
                Basic Information
              </h2>
              
              <div className="space-y-2">
                <Label htmlFor="title" className="font-medium text-[#1E3A5F]">
                  Event Title <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="title"
                  placeholder="e.g., Austin Craft Fair 2025"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className="input-default w-full"
                  data-testid="edit-event-title"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">
                    Event Type <span className="text-red-500">*</span>
                  </Label>
                  <Select value={formData.event_type} onValueChange={(v) => handleChange('event_type', v)}>
                    <SelectTrigger className="input-default" data-testid="edit-event-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-[#1E3A5F] rounded-xl bg-white">
                      {Object.entries(EVENT_TYPES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.label}</SelectItem>
                      ))}
                      <SelectItem value="custom">+ Add Custom Category</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {showCustomCategory && (
                  <div className="space-y-2">
                    <Label htmlFor="customCategory" className="font-medium text-[#1E3A5F]">
                      Custom Category Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="customCategory"
                      placeholder="e.g., Food Festival, Flea Market"
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      className="input-default w-full"
                      data-testid="edit-event-custom-category"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">Event Size</Label>
                  <Select value={formData.event_size} onValueChange={(v) => handleChange('event_size', v)}>
                    <SelectTrigger className="input-default" data-testid="edit-event-size">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-[#1E3A5F] rounded-xl bg-white">
                      {Object.entries(EVENT_SIZES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">Venue Type</Label>
                  <ComboboxWithAdd
                    options={venueTypes}
                    value={formData.venue_type}
                    onValueChange={(v) => handleChange('venue_type', v)}
                    onAddNew={handleAddVenueType}
                    placeholder="Select venue type"
                    searchPlaceholder="Search or add venue type..."
                    addNewText="Add venue type"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-6">
              <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] pb-2 border-b-2 border-[#E5E5E5]">
                Location
              </h2>

              <div className="space-y-2">
                <Label htmlFor="location" className="font-medium text-[#1E3A5F]">Venue Address</Label>
                <Input
                  id="location"
                  placeholder="e.g., Austin Convention Center, 500 E Cesar Chavez St"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  className="input-default w-full"
                  data-testid="edit-event-location"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="city" className="font-medium text-[#1E3A5F]">
                    City <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="city"
                    placeholder="e.g., Austin"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    className="input-default w-full"
                    data-testid="edit-event-city"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">
                    State <span className="text-red-500">*</span>
                  </Label>
                  <Select value={formData.state} onValueChange={(v) => handleChange('state', v)}>
                    <SelectTrigger className="input-default" data-testid="edit-event-state">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-[#1E3A5F] rounded-xl max-h-60 bg-white">
                      {US_STATES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Dates & Cost */}
            <div className="space-y-6">
              <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] pb-2 border-b-2 border-[#E5E5E5]">
                Dates & Cost
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">
                    Start Date <span className="text-red-500">*</span>
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "input-default w-full justify-start text-left font-normal",
                          !formData.date && "text-gray-400"
                        )}
                        data-testid="edit-event-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.date ? format(formData.date, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 border-2 border-[#1E3A5F] rounded-xl" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.date}
                        onSelect={(date) => handleChange('date', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "input-default w-full justify-start text-left font-normal",
                          !formData.end_date && "text-gray-400"
                        )}
                        data-testid="edit-event-end-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.end_date ? format(formData.end_date, 'PPP') : 'Same as start (optional)'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 border-2 border-[#1E3A5F] rounded-xl" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.end_date}
                        onSelect={(date) => handleChange('end_date', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Hours for primary date */}
              {formData.date && (
                <div className="space-y-2 mt-4 p-4 bg-[#F5F5F5] rounded-xl border border-[#E5E5E5]">
                  <Label className="font-medium text-[#1E3A5F] flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#1E3A5F]" />
                    Hours for {format(formData.date, 'EEE, MMM d')}{formData.end_date ? ` - ${format(formData.end_date, 'MMM d')}` : ''}
                  </Label>

                  {/* Precise start/end time (24-hour HH:MM) */}
                  <div className="grid grid-cols-2 gap-3 pb-2">
                    <div>
                      <Label htmlFor="edit-start-time" className="text-xs text-[#71717A]">Start time</Label>
                      <Input
                        id="edit-start-time"
                        type="time"
                        value={formData.start_time}
                        onChange={(e) => handleChange('start_time', e.target.value)}
                        className="input-default w-full mt-1"
                        data-testid="edit-event-start-time"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-end-time" className="text-xs text-[#71717A]">End time</Label>
                      <Input
                        id="edit-end-time"
                        type="time"
                        value={formData.end_time}
                        onChange={(e) => handleChange('end_time', e.target.value)}
                        className="input-default w-full mt-1"
                        data-testid="edit-event-end-time"
                      />
                    </div>
                  </div>

                  <Label className="text-xs text-[#71717A]">Or describe the hours (optional)</Label>
                  <Input
                    placeholder="e.g., 9am-5pm or Sat 9-5, Sun 10-4"
                    value={formData.date_hours}
                    onChange={(e) => handleChange('date_hours', e.target.value)}
                    className="input-default w-full"
                    data-testid="edit-event-date-hours"
                  />
                </div>
              )}

              {/* Farmers Market Recurring Schedule */}
              {isFarmersMarket && formData.date && formData.end_date && (
                <div className="space-y-4 mt-4 p-5 bg-emerald-50 rounded-xl border-2 border-emerald-300" data-testid="recurring-schedule-panel">
                  <div>
                    <Label className="font-bold text-emerald-800 text-base flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-emerald-600" />
                      Market Schedule
                    </Label>
                    <p className="text-xs text-emerald-700 mt-1">
                      Select which days and how often this market runs within {format(formData.date, 'MMM d')} - {format(formData.end_date, 'MMM d, yyyy')}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="font-medium text-emerald-800 text-sm">Day(s) of the Week</Label>
                    <div className="flex gap-2 flex-wrap">
                      {DAY_NAMES.map((day, idx) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => {
                            setRecurringDays(prev =>
                              prev.includes(idx) ? prev.filter(d => d !== idx) : [...prev, idx]
                            );
                          }}
                          className={`px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-all ${
                            recurringDays.includes(idx)
                              ? 'bg-emerald-600 border-emerald-600 text-white shadow-md'
                              : 'bg-white border-emerald-200 text-emerald-700 hover:border-emerald-400'
                          }`}
                          data-testid={`recurring-day-${idx}`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="font-medium text-emerald-800 text-sm">Frequency</Label>
                    <Select value={recurringFrequency} onValueChange={setRecurringFrequency}>
                      <SelectTrigger className="bg-white border-2 border-emerald-200 rounded-lg" data-testid="recurring-frequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-2 border-emerald-300 rounded-xl bg-white">
                        {FREQUENCY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {recurringDays.length > 0 && (
                    <div className="mt-3 p-3 bg-white rounded-lg border border-emerald-200" data-testid="recurring-preview">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-emerald-800">
                          {recurringPreview.length} market day{recurringPreview.length !== 1 ? 's' : ''}
                        </span>
                        <span className="text-xs text-emerald-600">
                          {recurringDays.map(d => DAY_NAMES[d]).join(', ')} — {FREQUENCY_OPTIONS.find(o => o.value === recurringFrequency)?.label}
                        </span>
                      </div>
                      {recurringPreview.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                          {recurringPreview.slice(0, 20).map((d, i) => (
                            <span key={i} className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                              {format(d, 'MMM d')}
                            </span>
                          ))}
                          {recurringPreview.length > 20 && (
                            <span className="text-xs text-emerald-600 font-medium">+{recurringPreview.length - 20} more</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Additional Date Ranges */}
              {formData.event_dates.length > 0 && (
                <div className="space-y-4 mt-4 p-4 bg-[#FFEDDF]/50 rounded-xl border border-[#1E3A5F]">
                  <Label className="font-medium text-[#1E3A5F]">Additional Dates</Label>
                  {formData.event_dates.map((dateRange, index) => (
                    <div key={index} className="space-y-3 p-3 bg-white rounded-lg border border-[#E5E5E5]">
                      <div className="flex items-center gap-4">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "input-default flex-1 justify-start text-left font-normal",
                                !dateRange.start_date && "text-gray-400"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {dateRange.start_date ? format(dateRange.start_date, 'MMM d, yyyy') : 'Start date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 border-2 border-[#1E3A5F] rounded-xl" align="start">
                            <Calendar
                              mode="single"
                              selected={dateRange.start_date}
                              onSelect={(date) => updateDateRange(index, 'start_date', date)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <span className="text-gray-500">to</span>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "input-default flex-1 justify-start text-left font-normal",
                                !dateRange.end_date && "text-gray-400"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {dateRange.end_date ? format(dateRange.end_date, 'MMM d, yyyy') : 'End date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 border-2 border-[#1E3A5F] rounded-xl" align="start">
                            <Calendar
                              mode="single"
                              selected={dateRange.end_date}
                              onSelect={(date) => updateDateRange(index, 'end_date', date)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeDateRange(index)}
                          className="text-red-500 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      {dateRange.start_date && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-[#1E3A5F]" />
                          <Input
                            placeholder="Hours (e.g., 10am-6pm)"
                            value={dateRange.hours || ''}
                            onChange={(e) => updateDateRange(index, 'hours', e.target.value)}
                            className="input-default flex-1"
                            data-testid={`edit-event-date-${index}-hours`}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              <Button
                type="button"
                variant="outline"
                onClick={addDateRange}
                className="mt-2 text-[#1E3A5F] border-dashed border-[#1E3A5F]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Additional Dates
              </Button>

              <div className="grid md:grid-cols-2 gap-6 mt-4">
                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">Application Deadline</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "input-default w-full justify-start text-left font-normal",
                          !formData.deadline && "text-gray-400"
                        )}
                        data-testid="edit-event-deadline"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.deadline ? format(formData.deadline, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 border-2 border-[#1E3A5F] rounded-xl" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.deadline}
                        onSelect={(date) => handleChange('deadline', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="booth_cost" className="font-medium text-[#1E3A5F]">Booth Cost ($)</Label>
                  <Input
                    id="booth_cost"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="e.g., 250"
                    value={formData.booth_cost}
                    onChange={(e) => handleChange('booth_cost', e.target.value)}
                    className="input-default w-full"
                    data-testid="edit-event-cost"
                  />
                </div>
              </div>
            </div>

            {/* Event Image */}
            <div className="space-y-6">
              <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] pb-2 border-b-2 border-[#E5E5E5]">
                Event Image
              </h2>

              <div className="space-y-4">
                {imagePreview ? (
                  <div className="relative w-full max-w-md">
                    <img
                      src={imagePreview}
                      alt="Event preview"
                      className="w-full h-48 object-cover rounded-xl border-2 border-[#1E3A5F]"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={removeImage}
                      data-testid="remove-image-btn"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label
                    className="flex flex-col items-center justify-center w-full max-w-md h-48 border-2 border-dashed border-[#1E3A5F] rounded-xl cursor-pointer hover:bg-[#F5F5F5] transition-colors"
                    data-testid="image-upload-area"
                  >
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleImageUpload}
                      className="hidden"
                      data-testid="image-upload-input"
                    />
                    {uploading ? (
                      <Loader2 className="w-10 h-10 text-[#1E3A5F] animate-spin" />
                    ) : (
                      <>
                        <Image className="w-10 h-10 text-[#1E3A5F] mb-2" />
                        <span className="text-[#4A4A4A] font-medium">Click to upload image</span>
                        <span className="text-sm text-[#71717A]">JPEG, PNG, GIF or WebP (max 5MB)</span>
                      </>
                    )}
                  </label>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-6">
              <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] pb-2 border-b-2 border-[#E5E5E5]">
                Description
              </h2>

              <div className="space-y-2">
                <Label htmlFor="description" className="font-medium text-[#1E3A5F]">Event Description</Label>
                <Textarea
                  id="description"
                  placeholder="Tell vendors about this event..."
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="input-default w-full min-h-[120px] py-3"
                  data-testid="edit-event-description"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="website_url" className="font-medium text-[#1E3A5F]">Website URL</Label>
                <Input
                  id="website_url"
                  type="url"
                  placeholder="https://..."
                  value={formData.website_url}
                  onChange={(e) => handleChange('website_url', e.target.value)}
                  className="input-default w-full"
                  data-testid="edit-event-website"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup_url" className="font-medium text-[#1E3A5F]">Application/Signup URL</Label>
                <Input
                  id="signup_url"
                  type="url"
                  placeholder="https://... (vendor application link)"
                  value={formData.signup_url}
                  onChange={(e) => handleChange('signup_url', e.target.value)}
                  className="input-default w-full"
                  data-testid="edit-event-signup-url"
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] pb-2 border-b-2 border-[#E5E5E5]">
                Contact Information
              </h2>

              <div className="space-y-2">
                <Label htmlFor="contact_name" className="font-medium text-[#1E3A5F]">Contact Name</Label>
                <Input
                  id="contact_name"
                  placeholder="e.g., John Smith"
                  value={formData.contact_name}
                  onChange={(e) => handleChange('contact_name', e.target.value)}
                  className="input-default w-full"
                  data-testid="edit-event-contact-name"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="contact_phone" className="font-medium text-[#1E3A5F]">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    type="tel"
                    placeholder="e.g., (555) 123-4567"
                    value={formData.contact_phone}
                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                    className="input-default w-full"
                    data-testid="edit-event-contact-phone"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_email" className="font-medium text-[#1E3A5F]">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    placeholder="contact@event.com"
                    value={formData.contact_email}
                    onChange={(e) => handleChange('contact_email', e.target.value)}
                    className="input-default w-full"
                    data-testid="edit-event-contact-email"
                  />
                </div>
              </div>
            </div>

            {/* Your Experience */}
            <div className="space-y-6">
              <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] pb-2 border-b-2 border-[#E5E5E5]">
                Your Experience
              </h2>
              
              <div className="grid grid-cols-1 gap-4">
                {/* Event Rating */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 font-medium text-[#1E3A5F]">
                    <Star className="w-4 h-4" />
                    Your Rating
                  </Label>
                  <p className="text-sm text-[#4A4A4A]">
                    Rate this event based on your experience
                  </p>
                  <EventRating
                    rating={formData.event_rating || 0}
                    onChange={(rating) => handleChange('event_rating', rating)}
                    size="lg"
                  />
                </div>
                
                {/* Personal Notes */}
                <div className="space-y-2">
                  <Label htmlFor="personal_notes" className="flex items-center gap-2 font-medium text-[#1E3A5F]">
                    <FileText className="w-4 h-4" />
                    Personal Notes
                  </Label>
                  <p className="text-sm text-[#4A4A4A]">
                    Record your thoughts, booth location, what sold well, etc.
                  </p>
                  <Textarea
                    id="personal_notes"
                    value={formData.personal_notes || ''}
                    onChange={(e) => handleChange('personal_notes', e.target.value)}
                    placeholder="e.g., Great location by the food vendors. Jewelry sold well. Consider bringing more earrings next year."
                    rows={4}
                    data-testid="edit-event-personal-notes"
                  />
                </div>
              </div>
            </div>

            {/* Community Sharing */}
            <div className="space-y-6">
              <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] pb-2 border-b-2 border-[#E5E5E5]">
                Community Sharing
              </h2>
              
              {(() => {
                // Check if event has passed (use end_date if available, otherwise use date)
                const eventEndDate = formData.end_date || formData.date;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const eventDate = eventEndDate ? new Date(eventEndDate) : null;
                const hasEventPassed = eventDate && eventDate < today;
                
                return (
                  <div className={`flex items-center justify-between p-4 rounded-xl border ${
                    hasEventPassed 
                      ? 'bg-gray-100 border-gray-300' 
                      : 'bg-[#FFEDDF]/50 border-[#1E3A5F]'
                  }`}>
                    <div className="flex items-start gap-3">
                      <Users className={`w-5 h-5 mt-0.5 ${hasEventPassed ? 'text-gray-400' : 'text-[#1E3A5F]'}`} />
                      <div>
                        <Label 
                          htmlFor="is_community" 
                          className={`font-medium ${hasEventPassed ? 'text-gray-500 cursor-not-allowed' : 'text-[#1E3A5F] cursor-pointer'}`}
                        >
                          Share as Community Event
                        </Label>
                        <p className={`text-sm mt-1 ${hasEventPassed ? 'text-gray-400' : 'text-[#4A4A4A]'}`}>
                          {hasEventPassed 
                            ? 'Past events cannot be shared with the community'
                            : 'Make this event visible to other AMSCompass members'
                          }
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="is_community"
                      checked={hasEventPassed ? false : formData.is_community}
                      onCheckedChange={(checked) => !hasEventPassed && handleChange('is_community', checked)}
                      disabled={hasEventPassed}
                      data-testid="edit-event-is-community"
                      className={hasEventPassed ? 'opacity-50 cursor-not-allowed' : ''}
                    />
                  </div>
                );
              })()}

              {formData.is_community && (
                <div
                  className="flex items-start gap-3 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl"
                  data-testid="community-share-warning"
                >
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-900">
                    <p className="font-semibold">Heads up — once you share, admins manage this event.</p>
                    <p className="text-amber-800/90 mt-0.5">
                      Sharing puts this event in the public community directory for every AMSCompass
                      vendor. From that point on only AMSCompass admins can edit or delete it. If you
                      spot anything that needs a change later, use{' '}
                      <a
                        href="mailto:support@amscompass.com?subject=Community%20event%20update%20request"
                        className="font-semibold underline"
                      >
                        support@amscompass.com
                      </a>{' '}
                      and we&rsquo;ll review the update.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-6">
              <Link to={`/events/${id}`} className="flex-1">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-2 border-[#1E3A5F] text-[#1E3A5F]"
                >
                  Cancel
                </Button>
              </Link>
              <Button
                type="submit"
                className="btn-primary flex-1"
                disabled={saving}
                data-testid="edit-event-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditEvent;
